import { useState, useCallback, useMemo } from 'react';

export const useFilters = (initialFilters = {}) => {
  const [filters, setFilters] = useState(initialFilters);

  // Handle individual filter changes
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    const clearedFilters = {};
    Object.keys(filters).forEach(key => {
      clearedFilters[key] = '';
    });
    setFilters(clearedFilters);
  }, [filters]);

  // Clear specific filter
  const clearFilter = useCallback((key) => {
    setFilters(prev => ({
      ...prev,
      [key]: ''
    }));
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some(value => value && value !== '');
  }, [filters]);

  // Get active filter count
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(value => value && value !== '').length;
  }, [filters]);

  // Get active filters with their keys
  const activeFilters = useMemo(() => {
    return Object.entries(filters)
      .filter(([key, value]) => value && value !== '')
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
  }, [filters]);

  // Bulk update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  }, []);

  // Reset to initial filters
  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  // Set filters completely (replace all)
  const setAllFilters = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  return {
    filters,
    handleFilterChange,
    clearFilters,
    clearFilter,
    hasActiveFilters,
    activeFilterCount,
    activeFilters,
    updateFilters,
    resetFilters,
    setFilters: setAllFilters
  };
};

// Additional hook for search functionality
export const useSearch = (data, searchFields = [], searchTerm = '') => {
  const filteredData = useMemo(() => {
    if (!searchTerm || !searchTerm.trim()) {
      return data;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    
    return data.filter(item => {
      return searchFields.some(field => {
        const value = getNestedValue(item, field);
        return value && value.toString().toLowerCase().includes(searchLower);
      });
    });
  }, [data, searchFields, searchTerm]);

  return filteredData;
};

// Helper function to get nested object values
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
};

// Hook for combining filters with search
export const useFilteredData = (data, filters, config) => {
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return [];
    }

    let result = [...data];

    // Apply search filter
    if (filters.search && filters.search.trim()) {
      const searchFields = getSearchFields(config);
      const searchLower = filters.search.toLowerCase().trim();
      
      result = result.filter(item => {
        return searchFields.some(field => {
          const value = getNestedValue(item, field);
          return value && value.toString().toLowerCase().includes(searchLower);
        });
      });
    }

    // Apply other filters
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'search' || !value || value === '') return;
      
      if (key === 'sort') {
        // Handle sorting
        const [sortBy, order = 'asc'] = value.split('-');
        result = result.sort((a, b) => {
          const aVal = getNestedValue(a, sortBy);
          const bVal = getNestedValue(b, sortBy);
          
          if (order === 'desc') {
            return bVal > aVal ? 1 : -1;
          }
          return aVal > bVal ? 1 : -1;
        });
      } else {
        // Handle regular filters
        result = result.filter(item => {
          const itemValue = getNestedValue(item, key);
          return itemValue === value;
        });
      }
    });

    return result;
  }, [data, filters, config]);

  return filteredData;
};

// Helper to extract search fields from config
const getSearchFields = (config) => {
  const searchConfig = config.find(filter => filter.key === 'search');
  return searchConfig?.searchFields || ['name', 'projectName', 'department', 'sector'];
};